/**
 * In-memory index of one IFC tileset's sidecar: elements by feature id, the
 * spatial nodes, the storey order and which storey each element belongs to.
 * Built once per tileset; every panel and the visibility logic read from it.
 */
import type { SidecarFile } from './sidecar';

export type IfcElement = {
	featureId: number;
	globalId: string;
	ifcClass: string;
	name: string;
	nodeId: string | null;
	bbox: number[] | null;
	properties: Record<string, string>;
	/** Lowercase name + GlobalId + every property value, for substring search. */
	haystack: string;
	/** True for a feature id the sidecar did not describe (gap filler). */
	placeholder?: true;
};

export type IfcNode = {
	nodeId: string;
	parentId: string | null;
	kind: string;
	name: string;
	elevation: number | null;
	children: string[];
};

export type IfcIndex = {
	/** Dense: elements[i].featureId === i. */
	elements: IfcElement[];
	byGlobalId: Map<string, number>;
	nodes: Map<string, IfcNode>;
	/** IfcBuildingStorey ids, highest elevation first; null elevations last in source order. */
	storeys: string[];
	/** Index into `storeys`, or -1 for unplaced elements and sidecars without hierarchy. */
	storeyOfElement: Int32Array;
	/** Every property key seen: IfcClass first, then alphabetical, Name and GlobalId last. */
	propertyKeys: string[];
	hasHierarchy: boolean;
};

export const STOREY_KIND = 'IfcBuildingStorey';
const MAX_PARENT_DEPTH = 64;
const SUMMARY_KEYS = ['IfcClass', 'Name', 'GlobalId'] as const;

export function buildIndex(sidecar: SidecarFile): IfcIndex {
	const nodes = new Map<string, IfcNode>();
	for (const raw of sidecar.hierarchy ?? []) {
		if (!raw || typeof raw.nodeId !== 'string') continue;
		nodes.set(raw.nodeId, {
			nodeId: raw.nodeId,
			parentId: typeof raw.parentId === 'string' ? raw.parentId : null,
			kind: raw.kind ?? '',
			name: raw.name ?? '',
			elevation: typeof raw.elevation === 'number' ? raw.elevation : null,
			children: [],
		});
	}
	for (const node of nodes.values()) {
		if (node.parentId) nodes.get(node.parentId)?.children.push(node.nodeId);
	}

	const storeys = orderStoreys([...nodes.values()].filter((n) => n.kind === STOREY_KIND));
	const storeyIndex = new Map(storeys.map((id, i) => [id, i]));

	for (const feature of sidecar.features) {
		const id = feature.featureId;
		if (!Number.isInteger(id) || id < 0) {
			throw new Error(`Invalid sidecar: bad featureId ${String(id)}`);
		}
	}
	const maxId = sidecar.features.reduce((max, f) => Math.max(max, f.featureId), -1);
	const elements: IfcElement[] = new Array(maxId + 1);
	const byGlobalId = new Map<string, number>();
	const keySet = new Set<string>();

	for (const feature of sidecar.features) {
		const id = feature.featureId;
		if (elements[id]) throw new Error(`Invalid sidecar: duplicate featureId ${id}`);

		const properties = feature.properties ?? {};
		const globalId = properties.GlobalId ?? feature.handle ?? '';
		const ifcClass = properties.IfcClass ?? feature.kind ?? '';
		const name = properties.Name ?? '';
		const bbox = Array.isArray(feature.bbox) && feature.bbox.length === 6 ? feature.bbox : null;

		elements[id] = {
			featureId: id,
			globalId,
			ifcClass,
			name,
			nodeId: feature.nodeId ?? null,
			bbox,
			properties,
			haystack: [name, globalId, ...Object.values(properties)].join('\n').toLowerCase(),
		};
		if (globalId) byGlobalId.set(globalId, id);
		for (const key of Object.keys(properties)) keySet.add(key);
	}

	for (let i = 0; i < elements.length; i++) {
		if (elements[i]) continue;
		elements[i] = {
			featureId: i,
			globalId: '',
			ifcClass: '',
			name: '',
			nodeId: null,
			bbox: null,
			properties: {},
			haystack: '',
			placeholder: true,
		};
	}

	const storeyOfElement = new Int32Array(elements.length).fill(-1);
	const storeyCache = new Map<string, number>();
	const storeyFor = (nodeId: string): number => {
		const cached = storeyCache.get(nodeId);
		if (cached !== undefined) return cached;
		let current: string | null = nodeId;
		let result = -1;
		for (let depth = 0; depth < MAX_PARENT_DEPTH && current; depth++) {
			const hit = storeyIndex.get(current);
			if (hit !== undefined) {
				result = hit;
				break;
			}
			current = nodes.get(current)?.parentId ?? null;
		}
		storeyCache.set(nodeId, result);
		return result;
	};
	for (const element of elements) {
		if (element.nodeId) storeyOfElement[element.featureId] = storeyFor(element.nodeId);
	}

	return {
		elements,
		byGlobalId,
		nodes,
		storeys,
		storeyOfElement,
		propertyKeys: orderKeys([...keySet]),
		hasHierarchy: nodes.size > 0,
	};
}

function orderStoreys(storeys: IfcNode[]): string[] {
	return storeys
		.map((node, sourceIndex) => ({ node, sourceIndex }))
		.sort((a, b) => {
			const ea = a.node.elevation;
			const eb = b.node.elevation;
			if (ea === null && eb === null) return a.sourceIndex - b.sourceIndex;
			if (ea === null) return 1;
			if (eb === null) return -1;
			return eb - ea || a.sourceIndex - b.sourceIndex;
		})
		.map(({ node }) => node.nodeId);
}

function orderKeys(keys: string[]): string[] {
	const summary = new Set<string>(SUMMARY_KEYS);
	const rest = keys.filter((k) => !summary.has(k)).sort((a, b) => a.localeCompare(b));
	const head = keys.includes('IfcClass') ? ['IfcClass'] : [];
	const tail = ['Name', 'GlobalId'].filter((k) => keys.includes(k));
	return [...head, ...rest, ...tail];
}
